import { useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { skipToken } from '@reduxjs/toolkit/query';
import { useNavigate } from 'react-router';
import { paths } from '@/constants/paths';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useLocalization } from '@/hooks/useLocalization';
import { isEmbeddableWidgetBundle } from '@/integrations/embeddableWidgetPortalUtils';
import { StandardWidgetProps } from '@/models/WidgetModels';
import { useGetAdvisorResourcesQuery } from '@/store/quickLinksResourcesApi';
import { ResourcesCarousel } from '@/widgets/resourcesCarousel';

export default function AdvisorResourcesWidget({
  title,
  isWidget,
  onDataLoaded,
  container = 'widget',
}: StandardWidgetProps) {
  const { t } = useLocalization();
  const navigate = useNavigate();

  const authLoaded = useAuthIsLoaded();
  const { data, isLoading, isFetching, isUninitialized, error } =
    useGetAdvisorResourcesQuery(authLoaded ? undefined : skipToken);
  const isPending = !authLoaded || isUninitialized || isLoading || isFetching;

  useEffect(() => {
    if (data || error) {
      onDataLoaded?.();
    }
  }, [data, error, onDataLoaded]);

  const viewAllFooter = isEmbeddableWidgetBundle ? null : (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Button
        variant='text'
        onClick={() => void navigate(paths.advisor.resources)}
        sx={{ fontWeight: 600 }}
      >
        {t('resources.viewAll')}
      </Button>
    </Box>
  );

  return (
    <ResourcesCarousel
      records={data?.Records}
      isLoading={isPending}
      hasError={!!error}
      title={title ?? t('resources')}
      errorText={t('resources.errorLoading')}
      emptyText={t('resources.noResourcesAvailable')}
      isWidget={isWidget}
      container={container}
      footer={viewAllFooter}
    />
  );
}
